/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Card,
  Row,
  Form,
  Col,
  Spin,
  Button,
  message,
  Grid,
  Tooltip,
  DatePicker,
} from 'antd';
import { DollarCircleOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import NumericInput from 'components/NumericInput';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import { getUnidadesDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';
import { abrirCorte, getCortes } from 'api/recaudacion/cortes';
import { getTerminalesDeNetPay } from 'api/configuracion/terminalDeNetPay';
import { getConfiguracionesDeSegmentosDeFolios } from 'api/configuracion/configuracionesDeFolios';
import { patchUsuario } from 'api/usuarios/usuarios';
import { DATETIME_FORMAT, DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import usePermissionMessage from 'hooks/usePermissionMessage';
import Moment from 'moment';

const ENV_NAME = process.env.REACT_APP_ENV_NAME || 'migob';
const requiredJuarez = ['view_configuraciondesegmentofolio', 'change_usuario'];

export const permissionConfig = {
  mainPermission: 'add_corte',
  required: [
    'view_corte',
    'view_unidadderecaudacion',
    'view_terminaldenetpay',
    'add_cortedianterior',
  ].concat(ENV_NAME === 'juarez' ? requiredJuarez : []),
};

const AbrirCorte = ({ permission }) => {
  const history = useHistory();
  const [form] = Form.useForm();
  const user = useSelector(({ auth }) => auth.user);
  const { collapsedSideBar, corte, entidad } = useSelector((state) => state.app);
  const screens = Grid.useBreakpoint();
  const [unidadesRecaudadoras, setUnidadRecaudadora] = useState([]);
  const [terminalNetPay, setTerminalNetPay] = useState([]);
  const [loading, setLoading] = useState(false);
  const [foliadores, setFoliadores] = useState();

  usePermissionMessage(permission);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      const requests = [
        getUnidadesDeRecaudacion({ puede_recaudar: true }),
        getTerminalesDeNetPay(),
      ];
      if (ENV_NAME === 'juarez') {
        requests.push(getConfiguracionesDeSegmentosDeFolios({ estados_globales: 4 }));
      }
      const [unidades, terminales, _foliadores = []] = await Promise.all(requests);
      if (mounted) {
        setUnidadRecaudadora(unidades);
        setTerminalNetPay(terminales);
        setFoliadores(_foliadores);
        const unidad_de_recaudacion = unidades.find((e) => e.id === user.unidad_de_recaudacion)?.id;
        form.setFieldsValue({
          terminal_de_netpay: user.terminal_netpay?.id,
          foliador: user?.foliador,
          unidad_de_recaudacion,
        });
      }
      if (corte?.apertura) {
        setTimeout(() => {
          history.push('/recaudacion/caja/');
          message.info('Ya se cuenta con un corte abierto');
        });
      }
      setLoading(false);
    };
    if (permission.hasAllViewPermission) {
      fetchData();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [corte]);

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      if (ENV_NAME === 'nayarit') {
        values.total_del_fondo = 0;
      }
      if (values.foliador && values.foliador !== user.foliador) {
        await patchUsuario(user.id, { foliador: values.foliador });
      }
      if (values?.apertura) {
        const cortes = await getCortes({
          apertura: values.apertura.format(DATETIME_FORMAT),
          usuario_cajero: user.id,
          unidad_de_recaudacion: values.unidad_de_recaudacion,
        });
        const yaExisteCorte = cortes.some((_corte) => _corte.apertura
          .isSame(values.apertura, 'day'));
        if (yaExisteCorte) {
          message.warn('El cajero ya cuenta con un corte en el día seleccionado, tiene que reabrirlo');
          setLoading(false);
          return;
        }
      }
      const created = await abrirCorte(values);
      if (created) {
        form.resetFields();
        setTimeout(() => {
          history.push('/recaudacion/caja/');
        });
      } else {
        setLoading(false);
      }
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rules = { required: [{ required: true }] };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Col span={24}>
          <Card
            bordered={false}
            className="form-container"
            title="Abrir Corte"
            extra={permission?.add_corte ? (
              <Button
                icon={<DollarCircleOutlined />}
                type="link"
                onClick={onFinish}
              >
                Abrir Corte
              </Button>
            ) : (
              <Tooltip title="No tienes permisos para abrir corte">
                <Button
                  icon={<DollarCircleOutlined />}
                  type="link"
                  onClick={onFinish}
                  disabled
                >
                  Abrir Corte
                </Button>
              </Tooltip>
            )}
          >
            <Col span={24}>
              <Form
                layout="vertical"
                form={form}
                scrollToFirstError
                initialValues={{
                  total_del_fondo: 0,
                }}
                onFinish={() => onFinish()}
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="unidad_de_recaudacion"
                      label={!collapsedSideBar && screens.lg ? 'Unidad de Recaudación' : 'UR'}
                      hasFeedback
                      rules={rules.required}
                    >
                      <Select dataSource={unidadesRecaudadoras} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={16} lg={8}>
                    <Form.Item
                      label="Terminal de Netpay"
                      name="terminal_de_netpay"
                      rules={entidad?.solo_pago_netpay ? rules.required : null}
                    >
                      <Select dataSource={terminalNetPay} />
                    </Form.Item>
                  </Col>
                  {permission.add_cortedianterior && (
                    <Col xs={24} sm={24} md={12} lg={8}>
                      <Form.Item
                        label="Fecha"
                        name="apertura"
                        hasFeedback
                        tooltip="Este campo no es requerido si se desea abrir el corte del día actual"
                      >
                        <DatePicker
                          format={DATE_FRONTEND_FORMAT}
                          allowClear
                          disabledDate={(current) => {
                            const today = new Moment();
                            const onMonthAgo = today?.subtract(2, 'month');
                            const _oneMonthAgo = onMonthAgo.subtract(1, 'day');
                            return current.isAfter(new Date()) || current.isBefore(_oneMonthAgo);
                          }}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {ENV_NAME !== 'nayarit' && (
                    <Col xs={24} sm={24} md={12} lg={8}>
                      <NumericInput
                        name="total_del_fondo"
                        label="Total de fondo (Efectivo)"
                        hasFeedback
                        decimal
                        currency
                        allowZero
                      />
                    </Col>
                  )}
                  {ENV_NAME === 'juarez' && (
                    <Col xs={24} sm={24} md={12} lg={8}>
                      <Form.Item
                        label="Foliador"
                        name="foliador"
                      >
                        <Select
                          dataSource={foliadores}
                          render={(e) => `${e.descripcion} (${e.separador})`}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Row>
              </Form>
            </Col>
          </Card>
        </Col>
      </Spin>
    </Row>
  );
};
export default AbrirCorte;
